/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  decideAssistantAction,
  fetchAssistantChat,
  fetchAssistantUsage,
  fetchEarlierAssistantMessages,
  pollAssistantMessage,
  postDayPlan,
  sendAssistantMessage,
  startCheckoutRescue,
} from '../actions/assistant';
import type {
  AbandonedCheckout,
  AssistantEvent,
  AssistantMessage,
  AssistantState,
} from '../types/assistant';

const initialState: AssistantState = {
  audience: null,
  isOpen: false,
  conversation: null,
  messages: [],
  chatLoaded: false,
  hasEarlier: false,
  usage: null,
  isSending: false,
  isPlanning: false,
  isLoadingChat: false,
  isLoadingEarlier: false,
  queuedPrompt: null,
  error: null,
  abandonedCheckout: null,
  chatCheckoutItemId: null,
};

const isFinal = (message?: AssistantMessage) =>
  message?.status === 'done' || message?.status === 'failed';

/** A server copy of a reply replaces ours unless ours is already further on. */
const replaceMessage = (state: AssistantState, next: AssistantMessage) => {
  const index = state.messages.findIndex((m) => m.id === next.id);
  if (index === -1) return;
  const current = state.messages[index];
  if (isFinal(current) && !isFinal(next)) return;
  state.messages[index] = { ...next, seq: current.seq };
};

const assistantSlice = createSlice({
  name: 'assistant',
  initialState,
  reducers: {
    openAssistant: (
      state,
      action: PayloadAction<{ prompt?: string } | undefined>,
    ) => {
      state.isOpen = true;
      if (action.payload?.prompt) state.queuedPrompt = action.payload.prompt;
    },
    closeAssistant: (state) => {
      state.isOpen = false;
      // A chat the app opened (a rescue) lasts while it is on screen: the
      // panel opens on the one chat next time.
      if (state.conversation?.kind) {
        state.conversation = null;
        state.messages = [];
        state.chatLoaded = false;
        state.hasEarlier = false;
      }
    },
    /** The audience the user's role now gets: a switch leaves the other side's chat behind. */
    setAssistantAudience: (state, action: PayloadAction<string>) =>
      state.audience === action.payload
        ? state
        : { ...initialState, audience: action.payload },
    clearQueuedPrompt: (state) => {
      state.queuedPrompt = null;
    },
    clearAssistantError: (state) => {
      state.error = null;
    },
    /** A Pro checkout was closed, or its payment sheet dismissed, unpaid. */
    checkoutAbandoned: {
      reducer: (state, action: PayloadAction<AbandonedCheckout>) => {
        state.abandonedCheckout = action.payload;
      },
      prepare: (reason?: string, path?: string) => ({
        payload: {
          at: Date.now(),
          reason,
          path: path ?? window.location.pathname + window.location.search,
        },
      }),
    },
    /** A chat card's Go Pro: the panel steps aside for the checkout. */
    openChatCheckout: (state, action: PayloadAction<string>) => {
      state.chatCheckoutItemId = action.payload;
      state.isOpen = false;
    },
    closeChatCheckout: (state) => {
      state.chatCheckoutItemId = null;
    },
    applyAssistantEvent: (state, action: PayloadAction<AssistantEvent>) => {
      const event = action.payload;
      const message = state.messages.find((m) => m.id === event.message_id);
      // A push for a chat not on screen: it reloads when shown again.
      if (!message || isFinal(message)) return;
      if (event.type === 'done' && event.message) {
        replaceMessage(state, event.message);
        return;
      }
      if ((message.seq ?? 0) >= event.seq) return;
      message.seq = event.seq;
      if (event.type === 'text' && typeof event.text === 'string') {
        message.status = 'streaming';
        message.content = event.text;
      } else if (event.type === 'step' && event.step) {
        const index = message.steps.findIndex((s) => s.id === event.step!.id);
        if (index === -1) message.steps.push(event.step);
        else message.steps[index] = event.step;
      } else if (event.type === 'block' && event.block) {
        if (!message.blocks.some((b) => b.id === event.block!.id)) {
          message.blocks.push(event.block);
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendAssistantMessage.pending, (state, action) => {
        state.isSending = true;
        state.error = null;
        // Shown at once; the server copy replaces it when the request lands.
        state.messages.push({
          id: action.meta.requestId,
          conversation_id: state.conversation?.id ?? '',
          role: 'user',
          status: 'done',
          content: action.meta.arg.message,
          blocks: [],
          steps: [],
        });
      })
      .addCase(sendAssistantMessage.fulfilled, (state, action) => {
        const { conversation, usage } = action.payload;
        state.isSending = false;
        state.conversation = conversation;
        state.usage = usage;
        state.messages = state.messages.filter(
          (m) => m.id !== action.meta.requestId,
        );
        state.messages.push(action.payload.user_message, {
          ...action.payload.assistant_message,
          seq: 0,
        });
      })
      .addCase(sendAssistantMessage.rejected, (state, action) => {
        state.isSending = false;
        state.messages = state.messages.filter(
          (m) => m.id !== action.meta.requestId,
        );
        state.error = (action.payload as string) ?? 'something_went_wrong';
        if (action.payload === 'assistant_daily_limit_reached' && state.usage) {
          state.usage.remaining = 0;
        }
        if (
          action.payload === 'rescue_chat_limit_reached' &&
          state.conversation
        ) {
          state.conversation.messages_left = 0;
        }
      })
      .addCase(postDayPlan.pending, (state) => {
        state.isPlanning = true;
        state.error = null;
      })
      .addCase(postDayPlan.fulfilled, (state, action) => {
        state.isPlanning = false;
        // A chat the app opened over the panel meanwhile keeps the screen.
        if (state.conversation?.kind) return;
        const { conversation, message } = action.payload;
        state.conversation = conversation;
        if (!state.messages.some((m) => m.id === message.id)) {
          state.messages.push({ ...message, seq: 0 });
        }
      })
      .addCase(postDayPlan.rejected, (state, action) => {
        state.isPlanning = false;
        if (action.payload === 'assistant_busy') state.error = 'assistant_busy';
      })
      .addCase(startCheckoutRescue.fulfilled, (state, action) => {
        const { conversation, messages } = action.payload;
        // Opened by the user meanwhile: keep what they are looking at.
        if (!conversation || state.isOpen) return;
        state.isOpen = true;
        state.conversation = conversation;
        state.messages = messages.map((m) => ({ ...m, seq: 0 }));
        state.chatLoaded = false;
        state.hasEarlier = false;
        state.error = null;
      })
      .addCase(fetchAssistantUsage.fulfilled, (state, action) => {
        state.usage = action.payload;
      })
      .addCase(fetchAssistantChat.pending, (state) => {
        state.isLoadingChat = true;
      })
      .addCase(fetchAssistantChat.fulfilled, (state, action) => {
        state.isLoadingChat = false;
        state.chatLoaded = true;
        // A chat the app opened over the panel meanwhile keeps the screen.
        if (state.conversation?.kind) return;
        state.conversation = action.payload.conversation;
        state.messages = action.payload.messages.map((m) => ({ ...m, seq: 0 }));
        state.hasEarlier = action.payload.hasEarlier;
      })
      .addCase(fetchAssistantChat.rejected, (state) => {
        state.isLoadingChat = false;
        // Not retried until the next visit: the chat still works from here.
        state.chatLoaded = true;
      })
      .addCase(fetchEarlierAssistantMessages.pending, (state) => {
        state.isLoadingEarlier = true;
      })
      .addCase(fetchEarlierAssistantMessages.fulfilled, (state, action) => {
        state.isLoadingEarlier = false;
        // The screen moved on (a rescue chat opened): the page belongs elsewhere.
        if (state.messages[0]?.id !== action.meta.arg) return;
        state.messages.unshift(
          ...action.payload.messages.map((m) => ({ ...m, seq: 0 })),
        );
        state.hasEarlier = action.payload.hasEarlier;
      })
      .addCase(fetchEarlierAssistantMessages.rejected, (state) => {
        state.isLoadingEarlier = false;
      })
      .addCase(pollAssistantMessage.fulfilled, (state, action) => {
        replaceMessage(state, action.payload);
      })
      .addCase(decideAssistantAction.pending, (state, action) => {
        const message = state.messages.find(
          (m) => m.id === action.meta.arg.messageId,
        );
        const block = message?.blocks.find(
          (b) => b.id === action.meta.arg.actionId,
        );
        if (
          block?.type === 'action' &&
          action.meta.arg.decision === 'confirm'
        ) {
          block.status = 'running';
        }
      })
      .addCase(decideAssistantAction.fulfilled, (state, action) => {
        const index = state.messages.findIndex(
          (m) => m.id === action.payload.id,
        );
        if (index !== -1) {
          state.messages[index] = {
            ...action.payload,
            seq: state.messages[index].seq,
          };
        }
      })
      .addCase(decideAssistantAction.rejected, (state, action) => {
        const message = state.messages.find(
          (m) => m.id === action.meta.arg.messageId,
        );
        const block = message?.blocks.find(
          (b) => b.id === action.meta.arg.actionId,
        );
        if (block?.type === 'action' && block.status === 'running') {
          block.status = 'pending';
        }
      });
  },
});

export const {
  openAssistant,
  closeAssistant,
  clearQueuedPrompt,
  clearAssistantError,
  setAssistantAudience,
  applyAssistantEvent,
  checkoutAbandoned,
  openChatCheckout,
  closeChatCheckout,
} = assistantSlice.actions;
export default assistantSlice.reducer;
